import { Sparkles } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { deleteAnalysis, listAnalysesOverview, patchAnalysisName } from '@/client/sdk.gen'
import type { AnalysisOverviewItem, AnalysisStatus } from '@/client/types.gen'
import { Button } from '@/components'
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button as UiButton } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { useSocket } from '@/context/SocketContext'
import { useMembershipRights } from '@/hooks/useMembershipRights'
import { useTranslation } from '@/hooks/useTranslation'
import { TOUR } from '@/lib/product-tour/constants'
import type { AnalysisStatusUpdateEvent } from '@/types/websocket'
import {
	extractErrorMessage,
	getClientResponseStatus,
	isErrorHandledByInterceptor,
} from '@/utils/error'
import { AnalysisTile } from './AnalysisTile'
import { AnalysisTileSkeleton } from './AnalysisTileSkeleton'

const OVERVIEW_SKELETON_KEYS = [
	'overview-skeleton-0',
	'overview-skeleton-1',
	'overview-skeleton-2',
	'overview-skeleton-3',
	'overview-skeleton-4',
	'overview-skeleton-5',
] as const

export interface AnalysisOverviewProps {
	projectId: string
	/** Set when the parent has loaded project info so workspace admins get edit affordances. */
	workspaceId?: string | null
	sortOrder:
		| 'alphabeticalAscending'
		| 'alphabeticalDescending'
		| 'createdAtNewest'
		| 'createdAtOldest'
	onCreateAnalysis?: () => void
	/** Bumped when user clicks global expand/collapse; tiles sync `sectionsExpandedFromSync` */
	sectionsSyncKey?: number
	sectionsExpandedFromSync?: boolean
}

export function AnalysisOverview({
	projectId,
	workspaceId,
	sortOrder,
	onCreateAnalysis,
	sectionsSyncKey = 0,
	sectionsExpandedFromSync = false,
}: AnalysisOverviewProps) {
	const { t } = useTranslation()
	const { canProjectEdit, canDeleteAnalysis: userCanDeleteAnalysisFlag } = useMembershipRights()
	const canEditName = canProjectEdit(projectId, workspaceId)
	const canDeleteAnalysis = userCanDeleteAnalysisFlag(projectId, workspaceId)
	const navigate = useNavigate()
	const [analysesList, setAnalysesList] = useState<AnalysisOverviewItem[]>([])
	const [analysesLoading, setAnalysesLoading] = useState(false)
	const [analysisPendingRename, setAnalysisPendingRename] = useState<AnalysisOverviewItem | null>(
		null
	)
	const [renameAnalysisName, setRenameAnalysisName] = useState('')
	const [renameError, setRenameError] = useState<string | null>(null)
	const [isRenaming, setIsRenaming] = useState(false)
	const [analysisPendingDelete, setAnalysisPendingDelete] = useState<AnalysisOverviewItem | null>(
		null
	)
	const [deleteConfirmText, setDeleteConfirmText] = useState('')
	const [isDeleting, setIsDeleting] = useState(false)
	const lastLoadedProjectIdRef = useRef<string | null>(null)
	const analysesListRef = useRef(analysesList)
	const analysesRefetchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const { socket, connected } = useSocket()

	const loadAnalyses = useCallback(async () => {
		if (!projectId) return
		setAnalysesLoading(true)
		try {
			const response = await listAnalysesOverview({
				path: { project_id: projectId },
				query: {
					skip: 0,
					limit: 100,
				},
			})

			if (!response.data) {
				throw new Error('Failed to load analyses')
			}
			setAnalysesList(response.data?.analyses || [])
		} catch (error: unknown) {
			console.error('Error loading analyses:', error)
			toast.error(t('pages.projectDetail.errors.failedToLoadAnalyses') || 'Failed to load analyses')
		} finally {
			setAnalysesLoading(false)
		}
	}, [projectId, t])

	useEffect(() => {
		analysesListRef.current = analysesList
	}, [analysesList])

	useEffect(() => {
		if (lastLoadedProjectIdRef.current === projectId) return
		lastLoadedProjectIdRef.current = projectId
		loadAnalyses()
	}, [projectId, loadAnalyses])

	useEffect(() => {
		if (!socket || !connected || !projectId) return

		const handleAnalysisStatusUpdate = (data: AnalysisStatusUpdateEvent) => {
			if (data.project_id && data.project_id !== projectId) return

			const analysisId = data.analysis_id
			const isKnown = analysesListRef.current.some((analysis) => analysis.id === analysisId)
			if (!isKnown) {
				if (analysesRefetchDebounceRef.current) {
					clearTimeout(analysesRefetchDebounceRef.current)
				}
				analysesRefetchDebounceRef.current = setTimeout(() => {
					analysesRefetchDebounceRef.current = null
					void loadAnalyses()
				}, 300)
				return
			}

			setAnalysesList((prev) =>
				prev.map((analysis) =>
					analysis.id === analysisId ? { ...analysis, status: data.status as AnalysisStatus } : analysis
				)
			)
		}

		socket.on('analysis:status:update', handleAnalysisStatusUpdate)

		return () => {
			socket.off('analysis:status:update', handleAnalysisStatusUpdate)
			if (analysesRefetchDebounceRef.current) {
				clearTimeout(analysesRefetchDebounceRef.current)
				analysesRefetchDebounceRef.current = null
			}
		}
	}, [socket, connected, projectId, loadAnalyses])

	const sortedAnalyses = useMemo(() => {
		const analysesWithMeta = analysesList.map((analysis) => ({
			analysis,
			name: (analysis.name || '').toLowerCase(),
			createdAt: analysis.created_at ? new Date(analysis.created_at).getTime() : Number.NaN,
		}))

		const sorted = analysesWithMeta
			.sort((leftAnalysisMeta, rightAnalysisMeta) => {
				const { name: leftAnalysisName, createdAt: leftAnalysisCreatedAt } = leftAnalysisMeta
				const { name: rightAnalysisName, createdAt: rightAnalysisCreatedAt } = rightAnalysisMeta

				if (sortOrder === 'createdAtNewest' || sortOrder === 'createdAtOldest') {
					const hasValidLeftCreatedAt = Number.isFinite(leftAnalysisCreatedAt)
					const hasValidRightCreatedAt = Number.isFinite(rightAnalysisCreatedAt)

					if (
						hasValidLeftCreatedAt &&
						hasValidRightCreatedAt &&
						leftAnalysisCreatedAt !== rightAnalysisCreatedAt
					) {
						return sortOrder === 'createdAtNewest'
							? rightAnalysisCreatedAt - leftAnalysisCreatedAt
							: leftAnalysisCreatedAt - rightAnalysisCreatedAt
					}

					if (hasValidLeftCreatedAt !== hasValidRightCreatedAt) {
						return hasValidLeftCreatedAt ? -1 : 1
					}
				}

				if (leftAnalysisName === rightAnalysisName) return 0

				if (sortOrder === 'alphabeticalAscending') {
					return leftAnalysisName < rightAnalysisName ? -1 : 1
				}

				if (sortOrder === 'alphabeticalDescending') {
					return leftAnalysisName > rightAnalysisName ? -1 : 1
				}

				return leftAnalysisName < rightAnalysisName ? -1 : 1
			})
			.map((item) => item.analysis)

		return sorted
	}, [analysesList, sortOrder])

	const deleteConfirmRequiredLower = t(
		'pages.projectDetail.analyses.delete.confirmRequiredText'
	).toLowerCase()

	const handleRename = useCallback((analysis: AnalysisOverviewItem) => {
		setAnalysisPendingRename(analysis)
		setRenameAnalysisName(analysis.name)
		setRenameError(null)
	}, [])

	const handleConfirmRename = useCallback(async () => {
		if (!analysisPendingRename) return

		const next = renameAnalysisName.trim()
		if (next.length === 0) {
			setRenameError(t('pages.projectDetail.analyses.nameRequired'))
			return
		}
		if (next.length > 255) {
			setRenameError(t('pages.projectDetail.analyses.nameTooLong'))
			return
		}
		if (next === analysisPendingRename.name) {
			setAnalysisPendingRename(null)
			setRenameAnalysisName('')
			setRenameError(null)
			return
		}

		setIsRenaming(true)
		setRenameError(null)
		try {
			const res = await patchAnalysisName({
				path: { project_id: projectId, analysis_id: analysisPendingRename.id },
				body: { name: next },
			})
			if (res.data) {
				setAnalysesList((prev) =>
					prev.map((a) => (a.id === analysisPendingRename.id ? { ...a, name: next } : a))
				)
				toast.success(t('pages.projectDetail.analyses.renameSuccess'))
				setAnalysisPendingRename(null)
				setRenameAnalysisName('')
				return
			}
			if (getClientResponseStatus(res) === 409) {
				setRenameError(t('pages.projectDetail.analyses.nameDuplicate'))
				return
			}
			const det = (res.error as { detail?: { code?: string } } | undefined)?.detail
			if (typeof det === 'object' && det && 'code' in det && det.code === 'analysis.name.duplicate') {
				setRenameError(t('pages.projectDetail.analyses.nameDuplicate'))
				return
			}
			if (isErrorHandledByInterceptor(res.error)) return
			toast.error(extractErrorMessage(res.error, t('pages.projectDetail.analyses.renameFailed')))
		} catch (error: unknown) {
			if (isErrorHandledByInterceptor(error)) return
			toast.error(extractErrorMessage(error, t('pages.projectDetail.analyses.renameFailed')))
		} finally {
			setIsRenaming(false)
		}
	}, [analysisPendingRename, projectId, renameAnalysisName, t])

	const handleConfirmDeleteAnalysis = useCallback(async () => {
		if (!analysisPendingDelete) return
		setIsDeleting(true)
		try {
			const response = await deleteAnalysis({
				path: { project_id: projectId, analysis_id: analysisPendingDelete.id },
			})
			if (response.error) {
				if (isErrorHandledByInterceptor(response.error)) return
				toast.error(
					extractErrorMessage(response.error, t('pages.projectDetail.analyses.delete.failed'))
				)
				return
			}
			if (!response.data?.success) {
				toast.error(t('pages.projectDetail.analyses.delete.failed'))
				return
			}
			const removedId = analysisPendingDelete.id
			setAnalysesList((prev) => prev.filter((a) => a.id !== removedId))
			toast.success(t('pages.projectDetail.analyses.delete.success'))
			setAnalysisPendingDelete(null)
			setDeleteConfirmText('')
		} catch (error: unknown) {
			if (isErrorHandledByInterceptor(error)) return
			toast.error(extractErrorMessage(error, t('pages.projectDetail.analyses.delete.failed')))
		} finally {
			setIsDeleting(false)
		}
	}, [analysisPendingDelete, projectId, t])

	if (analysesLoading) {
		return (
			<div className="flex flex-col gap-4" aria-busy="true" role="status">
				<span className="sr-only">{t('common.loading') || 'Loading...'}</span>
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
					{OVERVIEW_SKELETON_KEYS.map((k) => (
						<AnalysisTileSkeleton key={k} />
					))}
				</div>
			</div>
		)
	}

	if (sortedAnalyses.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center gap-3 text-center">
				<p className="text-gray-500">{t('pages.projectDetail.noAnalyses') || 'No analyses yet'}</p>
				{onCreateAnalysis && (
					<Button onClick={onCreateAnalysis} data-tour={TOUR.startAnalysis}>
						<Sparkles className="w-5 h-5" aria-hidden />
						{t('pages.projectDetail.modal.title')}
					</Button>
				)}
			</div>
		)
	}

	return (
		<>
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
				{sortedAnalyses.map((analysis) => (
					<AnalysisTile
						key={analysis.id}
						analysis={analysis}
						canEditName={canEditName}
						canDeleteAnalysis={canDeleteAnalysis}
						sectionsSyncKey={sectionsSyncKey}
						sectionsExpandedFromSync={sectionsExpandedFromSync}
						onRename={canEditName ? () => handleRename(analysis) : undefined}
						onDelete={canDeleteAnalysis ? () => setAnalysisPendingDelete(analysis) : undefined}
						onClick={() => navigate(`/projects/${projectId}/analyses/${analysis.id}`)}
					/>
				))}
			</div>

			<AlertDialog
				open={analysisPendingRename !== null}
				onOpenChange={(open) => {
					if (!open && !isRenaming) {
						setAnalysisPendingRename(null)
						setRenameAnalysisName('')
						setRenameError(null)
					}
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t('pages.projectDetail.analyses.dialogs.rename.title')}</AlertDialogTitle>
						<AlertDialogDescription>
							{t('pages.projectDetail.analyses.dialogs.rename.description')}
						</AlertDialogDescription>
					</AlertDialogHeader>

					<div className="flex flex-col gap-2">
						<Label htmlFor="analysis-rename-input">
							{t('pages.projectDetail.analyses.dialogs.rename.analysisName')}
						</Label>

						<Input
							id="analysis-rename-input"
							type="text"
							value={renameAnalysisName}
							onChange={(e) => {
								setRenameAnalysisName(e.target.value)
								setRenameError(null)
							}}
							placeholder={t('pages.projectDetail.analyses.dialogs.rename.analysisNamePlaceholder')}
							maxLength={255}
							disabled={isRenaming}
							aria-invalid={renameError != null}
						/>

						{renameError && <p className="text-sm text-[#ff3131]">{renameError}</p>}
					</div>

					<AlertDialogFooter>
						<AlertDialogCancel
							className="hover:scale-105 active:scale-95 transition-transform duration-200 ease-in-out"
							type="button"
							disabled={isRenaming}
						>
							{t('common.cancel')}
						</AlertDialogCancel>

						<UiButton
							className="hover:scale-105 active:scale-95 transition-transform duration-200 ease-in-out"
							type="button"
							onClick={() => void handleConfirmRename()}
							disabled={isRenaming || !renameAnalysisName.trim()}
						>
							{isRenaming ? (
								<>
									<Spinner className="size-4 mr-2" aria-hidden />
									{t('pages.projectDetail.analyses.dialogs.rename.saving')}
								</>
							) : (
								t('pages.projectDetail.analyses.dialogs.rename.save')
							)}
						</UiButton>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<AlertDialog
				open={analysisPendingDelete !== null}
				onOpenChange={(open) => {
					if (!open) {
						setAnalysisPendingDelete(null)
						setDeleteConfirmText('')
					}
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t('pages.projectDetail.analyses.delete.title')}</AlertDialogTitle>
						<AlertDialogDescription>
							{t('pages.projectDetail.analyses.delete.description')}
						</AlertDialogDescription>
					</AlertDialogHeader>

					<div className="flex flex-col gap-2">
						<Label htmlFor="analysis-delete-confirm">
							{t('pages.projectDetail.analyses.delete.confirmPrompt')}
						</Label>

						<Input
							id="analysis-delete-confirm"
							type="text"
							value={deleteConfirmText}
							onChange={(e) => setDeleteConfirmText(e.target.value)}
							placeholder={t('pages.projectDetail.analyses.delete.confirmPlaceholder')}
							className="font-mono"
							disabled={isDeleting}
						/>
					</div>

					<AlertDialogFooter>
						<AlertDialogCancel
							className="hover:scale-105 active:scale-95 transition-transform duration-200 ease-in-out"
							type="button"
							disabled={isDeleting}
						>
							{t('common.cancel')}
						</AlertDialogCancel>

						<UiButton
							variant="destructive"
							className="hover:scale-105 active:scale-95 transition-transform duration-200 ease-in-out"
							type="button"
							onClick={() => void handleConfirmDeleteAnalysis()}
							disabled={
								isDeleting || deleteConfirmText.trim().toLowerCase() !== deleteConfirmRequiredLower
							}
						>
							{isDeleting ? (
								<>
									<Spinner className="size-4 mr-2" aria-hidden />
									{t('pages.projectDetail.analyses.delete.deleting')}
								</>
							) : (
								t('pages.projectDetail.analyses.delete.delete')
							)}
						</UiButton>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}
